import React, { useEffect, useState } from "react";

import {
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  TextFieldProps,
} from "@mui/material";
import {
  DatePickerProps,
  DatePicker as MuiDatePicker,
} from "@mui/x-date-pickers";

import { parse } from "date-fns";

import { getLocalDate } from "@utils/date";
import { cn } from "@utils/tailwind";

export const DefaultTextField = React.forwardRef<any, TextFieldProps>(
  ({ className, ...props }, ref) => (
    <TextField
      ref={ref}
      color="secondary"
      type="text"
      size="small"
      variant="outlined"
      {...props}
      className={cn("[&_.MuiOutlinedInput-root]:bg-white", className)}
    />
  )
);

type CustomDatePickerProps = DatePickerProps<Date> & {
  onChange?: (date: Date | null) => void;
};

const getDate = (date: Date | string | null | undefined): Date | null => {
  if (!date) return null;

  if (date instanceof Date) {
    return date;
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return getLocalDate(date);
  } else {
    console.warn("Deprecated date format provided %s", date);
    return parse(date, "MM/dd/yyyy", new Date());
  }
};

/**
 * @deprecated This is an implementation that uses `value`, should in favor be using a controlled date picker
 **/
export const DatePicker = React.forwardRef<
  HTMLDivElement,
  CustomDatePickerProps
>(({ onChange, value, ...props }, ref) => {
  const [date, setDate] = useState<Date | null>(getDate(value));

  useEffect(() => {
    setDate(getDate(value));
  }, [value]);

  return (
    <MuiDatePicker
      ref={ref}
      minDate={getLocalDate("2016-01-01")}
      maxDate={getLocalDate("2031-01-01")}
      value={date}
      onAccept={onChange}
      onChange={(value, context) => {
        if (!context.validationError) {
          setDate(value);
        }
      }}
      {...props}
      slotProps={
        {
          ...props.slotProps,
          field: {
            size: "small",
            ...props.slotProps?.field,
            onBlur: () => {
              onChange?.(date);
            },
            onKeyDown: (e) => {
              if (e.key === "Enter") {
                onChange?.(date);
              }
            },
          },
        } as any
      }
    />
  );
});

type SelectFieldProps = {
  options: {
    label?: string;
    value?: string | number;
    id?: string | number;
    name?: string;
    header?: boolean;
  }[];
} & React.ComponentProps<typeof Select>;
export const SelectField = ({
  label,
  options,
  className,
  ...props
}: SelectFieldProps) => {
  const labelId = React.useId();
  return (
    <FormControl className={className} size="small" fullWidth={props.fullWidth}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select labelId={labelId} label={label} {...props}>
        {options.map((o, i) =>
          o.header ? (
            <ListSubheader key={i}>{o.label}</ListSubheader>
          ) : (
            <MenuItem key={`${o.value ?? o.id}-${i}`} value={o.value ?? o.id}>
              {o.label ?? o.name}
            </MenuItem>
          )
        )}
      </Select>
    </FormControl>
  );
};
